import { GridValueGetterParams } from '@mui/x-data-grid';
import { getObjectValueByPath } from 'helpers/general';
import { DataTableColumn } from '../../DataTable';
import { BackOfficeSession } from '../../opsMapping';
import { GridToolbarExportButtonProps } from './GridToolbarExportButton';

export const formatCsvFileName = (prefix: string) => {
    prefix = typeof prefix !== 'undefined' ? prefix : '';
    const exportDate = new Date().toLocaleDateString('en-us');

    return `TankUtility_${prefix}_${exportDate}.csv`;
};

const downloadCsvFile = (filename: string, csvContent: string) => {
    const hiddenElement = document.createElement('a');
    hiddenElement.href = 'data:attachment/csv,' + encodeURI(csvContent);
    hiddenElement.target = '_blank';
    hiddenElement.download = filename;
    document.body.appendChild(hiddenElement); // Required for FF
    hiddenElement.click();
    // Garbage collection
    window.URL.revokeObjectURL(hiddenElement.href);
    document.body.removeChild(hiddenElement);
};

const mapSessionHeaders = (
    visibilityModel: BackOfficeSession['visibilityModel'],
    columns: DataTableColumn[]
) => {
    const shownFields = Object.entries(visibilityModel)
        .filter(([_, value]) => value === true)
        .map(([field]) => field)
        .filter((f) => {
            const associatedColumn = columns.find((col) => col.field === f);
            return associatedColumn?.disableExport ? false : true;
        });

    const shownColumnHeaders = shownFields.map(
        (field) =>
            columns.find((col) => col.field === field)?.headerName ?? field
    );

    const shownColumns = shownFields
        .map(
            (field) =>
                columns.find((col) => col.field === field) ??
                ({} as DataTableColumn)
        )
        .filter((f) => !!f);

    return { shownFields, shownColumnHeaders, shownColumns };
};

const defaultRowFormatter = <TData extends Record<string, any>>(
    rawRow: TData,
    shownFields: string[],
    valueFormatter: (field: string, value: any, row: Record<string, any>) => any
) => {
    return shownFields.map((field) => {
        const value = getObjectValueByPath(rawRow, field);
        if (!value) return '';
        const formattedValue = valueFormatter(field, value, rawRow);

        return formattedValue;
    });
};

export const mapDataToCsv = async (
    query: () => Promise<(any | undefined)[]>,

    visibilityModel: BackOfficeSession['visibilityModel'],
    columns: DataTableColumn[],
    gridId: string,
    csvOptions: GridToolbarExportButtonProps['csvOptions']
) => {
    const { shownFields, shownColumnHeaders, shownColumns } = mapSessionHeaders(
        visibilityModel,
        columns
    );

    const rowFormatter = csvOptions?.rowFormatter ?? defaultRowFormatter;

    const valueFormatter = (
        field: string,
        value: any,
        row: Record<string, any>
    ) => {
        const params = {
            value,
            row,
        };

        const associatedColumn = shownColumns.find((c) => c.field === field);

        const formatter =
            associatedColumn?.valueFormatter ??
            associatedColumn?.valueGetter ??
            ((params) => params.value);

        const formattedValue = formatter(params as GridValueGetterParams);

        return formattedValue;
    };

    const rows = await query().then((results) => {
        return results?.map((rawResult) => {
            return rowFormatter(rawResult, shownFields, valueFormatter);
        });
    });

    const formattedCsvString = [shownColumnHeaders, ...rows]
        .map((row) => {
            const out = `${[
                ...row.map((e) =>
                    typeof e === 'string' && e.includes(',') ? `"${e}"` : e
                ),
            ].join(',')}\r\n`;
            return out;
        })
        .join('')
        .replace(/[#]/g, '');

    downloadCsvFile(
        formatCsvFileName(csvOptions?.fileName ?? gridId),
        formattedCsvString
    );
};
